import { useState } from "react";
import { CandidateService } from "@/services/CandidateService";
import { toaster } from "@/components/ui/toaster";
import AddNoteModal from "../components/candidate/AddNoteModal";
import ChangeStatusModal from "../components/candidate/ChangeStatusModal";
import BulkEmailModal from "../components/candidate/BulkEmailModal";
import AddTimelineEntryModal from "../components/candidate/AddTimelineEntryModal";

const useCandidateSingleActions = ({ t, onStatusChanged, onNoteAdded, onTimelineEntryAdded } = {}) => {
    const [activeCandId, setActiveCandId] = useState(null);
    const [activeCandStatus, setActiveCandStatus] = useState(null);

    const [showStatusModal, setShowStatusModal] = useState(false);
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [showNoteModal, setShowNoteModal] = useState(false);

    const [isStatusLoading, setIsStatusLoading] = useState(false);
    const [isEmailLoading, setIsEmailLoading] = useState(false);
    const [isNoteLoading, setIsNoteLoading] = useState(false);

    const [noteContent, setNoteContent] = useState("");
    const [noteFiles, setNoteFiles] = useState([]);

    const [showTimelineModal, setShowTimelineModal] = useState(false);
    const [isTimelineLoading, setIsTimelineLoading] = useState(false);
    const [timelineKind, setTimelineKind] = useState("");
    const [timelineDescription, setTimelineDescription] = useState("");
    const [timelineOccurredAt, setTimelineOccurredAt] = useState("");
    const [timelineFiles, setTimelineFiles] = useState([]);

    const openStatusModal = (candId, currentStatus) => {
        setActiveCandId(candId);
        setActiveCandStatus(currentStatus);
        setShowStatusModal(true);
    };

    const openEmailModal = (candId) => {
        setActiveCandId(candId);
        setShowEmailModal(true);
    };

    const openNoteModal = (candId) => {
        setActiveCandId(candId);
        setShowNoteModal(true);
    };

    const closeNoteModal = () => {
        setShowNoteModal(false);
        setNoteContent("");
        setNoteFiles([]);
    };

    const openTimelineModal = (candId) => {
        setActiveCandId(candId);
        setTimelineKind("");
        setTimelineDescription("");
        setTimelineFiles([]);
        setShowTimelineModal(true);
    };

    const closeTimelineModal = () => {
        setShowTimelineModal(false);
        setTimelineKind("");
        setTimelineDescription("");
        setTimelineOccurredAt("");
        setTimelineFiles([]);
    };

    const handleChangeStatus = async (newStatus, note) => {
        setIsStatusLoading(true);
        try {
            const updated = await CandidateService.changeStatus(activeCandId, newStatus, note || null);
            toaster.create({ type: "success", title: t("candidateDetailPage.statusUpdateSuccess") });
            setShowStatusModal(false);
            onStatusChanged?.(activeCandId, updated);
        } catch {
            toaster.create({ type: "error", title: t("candidateDetailPage.statusUpdateError") });
        } finally {
            setIsStatusLoading(false);
        }
    };

    const handleSendEmail = async (subject, body, files) => {
        setIsEmailLoading(true);
        try {
            const result = await CandidateService.bulkSendEmail([activeCandId], subject, body, files);
            toaster.create({
                type: result.failureCount > 0 ? "warning" : "success",
                title: t("candidatePage.bulkEmailSuccess", {
                    success: result.successCount,
                    total: result.successCount + result.failureCount,
                }),
            });
            setShowEmailModal(false);
        } catch {
            toaster.create({ type: "error", title: t("candidatePage.bulkEmailError") });
        } finally {
            setIsEmailLoading(false);
        }
    };

    const handleAddNote = async () => {
        if (!noteContent.trim()) return;
        setIsNoteLoading(true);
        try {
            const note = await CandidateService.addNote(activeCandId, noteContent.trim(), noteFiles);
            toaster.create({ type: "success", title: t("candidateDetailPage.noteAddSuccess") });
            setShowNoteModal(false);
            setNoteContent("");
            setNoteFiles([]);
            onNoteAdded?.(activeCandId, note);
        } catch {
            toaster.create({ type: "error", title: t("candidateDetailPage.noteAddError") });
        } finally {
            setIsNoteLoading(false);
        }
    };

    const handleAddTimelineEntry = async () => {
        if (!timelineKind || !timelineDescription.trim()) return;
        setIsTimelineLoading(true);
        try {
            await CandidateService.addTimelineEntry(
                activeCandId,
                timelineKind,
                timelineDescription.trim(),
                new Date(timelineOccurredAt).toISOString(),
                timelineFiles
            );
            toaster.create({ type: "success", title: t("candidateDetailPage.timelineEntrySuccess") });
            closeTimelineModal();
            onTimelineEntryAdded?.(activeCandId);
        } catch {
            toaster.create({ type: "error", title: t("candidateDetailPage.timelineEntryError") });
        } finally {
            setIsTimelineLoading(false);
        }
    };

    const modals = (
        <>
            <ChangeStatusModal
                isOpen={showStatusModal}
                onClose={() => setShowStatusModal(false)}
                currentStatus={activeCandStatus}
                onSubmit={handleChangeStatus}
                isLoading={isStatusLoading}
            />
            <BulkEmailModal
                isOpen={showEmailModal}
                onClose={() => setShowEmailModal(false)}
                title={t("candidateDetailPage.sendEmailModalTitle")}
                onSubmit={handleSendEmail}
                isLoading={isEmailLoading}
            />
            <AddNoteModal
                isOpen={showNoteModal}
                onClose={closeNoteModal}
                noteContent={noteContent}
                setNoteContent={setNoteContent}
                noteFiles={noteFiles}
                setNoteFiles={setNoteFiles}
                onSubmit={handleAddNote}
                isLoading={isNoteLoading}
            />
            <AddTimelineEntryModal
                isOpen={showTimelineModal}
                onClose={closeTimelineModal}
                kind={timelineKind}
                setKind={setTimelineKind}
                description={timelineDescription}
                setDescription={setTimelineDescription}
                occurredAt={timelineOccurredAt}
                setOccurredAt={setTimelineOccurredAt}
                files={timelineFiles}
                setFiles={setTimelineFiles}
                onSubmit={handleAddTimelineEntry}
                isLoading={isTimelineLoading}
            />
        </>
    );

    return { openStatusModal, openEmailModal, openNoteModal, closeNoteModal, openTimelineModal, modals };
};

export default useCandidateSingleActions;
